import { useQuery } from "@tanstack/react-query";

import { usePermissions } from "@/contexts/PermissionsContext";
import {
  getTikTokShopStatus,
  type TikTokFeatureKey,
  type TikTokFeatureMap,
  type TikTokShopStatus,
} from "@/services/tiktokShop.service";
import { PlanFeature } from "@/types/permissions";

/** PRE_RELEASE_TIKTOK: what the dashboard assumes before the status loads. */
const NO_FEATURES: TikTokFeatureMap = {
  analytics: "coming_soon",
  creatorSearch: "coming_soon",
  samples: "coming_soon",
  adRequests: "coming_soon",
  invites: "coming_soon",
  messages: "coming_soon",
};

export interface TikTokAccess {
  /** Permissions or the status call still in flight. */
  isLoading: boolean;
  /**
   * The TikTok Shop module exists for this brand: the plan feature (or the
   * pre-release grant, which implies it) AND the status endpoint answered.
   * Outside the pre-release the endpoint 404s, so this stays false and no
   * TikTok surface renders.
   */
  enabled: boolean;
  /** PRE_RELEASE_TIKTOK: the admin let this brand in early. */
  preRelease: boolean;
  /** PRE_RELEASE_TIKTOK: per-surface state the UI renders from. */
  features: TikTokFeatureMap;
  status: TikTokShopStatus | undefined;
  /** Either app ACTIVE. */
  connected: boolean;
  dataConnected: boolean;
  affiliateConnected: boolean;
  refetch: () => void;
}

/**
 * The one place that decides whether TikTok Shop surfaces render for the
 * signed-in brand. Every TikTok view (sidebar, dashboard section, integrations
 * card, creators tab, TikTok page) reads this instead of a build-time flag.
 * React-query dedupes the status fetch across them by key.
 *
 * PRE_RELEASE_TIKTOK: at GA this collapses to the plan-feature check plus
 * the status fetch; `preRelease` and `features` go away.
 */
export function useTikTokAccess(): TikTokAccess {
  const { hasFeature, isLoading: permissionsLoading } = usePermissions();
  const allowed = !permissionsLoading && hasFeature(PlanFeature.TIKTOK_SHOP);
  const query = useQuery({
    queryKey: ["tiktok-shop-status"],
    queryFn: getTikTokShopStatus,
    enabled: allowed,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const status = query.data;
  const dataConnected = !!status?.data.connected;
  const affiliateConnected = !!status?.affiliate.connected;
  return {
    isLoading: permissionsLoading || (allowed && query.isLoading),
    enabled: allowed && !!status,
    preRelease: status?.preRelease ?? false,
    features: status?.features ?? NO_FEATURES,
    status,
    connected: dataConnected || affiliateConnected,
    dataConnected,
    affiliateConnected,
    refetch: () => void query.refetch(),
  };
}

/** True when the surface is usable today (live or beta). */
export const isTikTokFeatureOn = (features: TikTokFeatureMap, key: TikTokFeatureKey) =>
  features[key] !== "coming_soon";
